/**
 * Composant pour afficher les pages personnalisées dans le frontend React
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Navbar from '../components/Navbar';
import logger from '../utils/logger';

const CustomPageViewer = () => {
  const { '*': pagePath } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pageContent, setPageContent] = useState(null);
  const [error, setError] = useState(null);

  const updateMetaTags = (pageData) => {
    // Fonction utilitaire pour créer ou mettre à jour un meta tag
    const setMetaTag = (name, content, property = false) => {
      if (!content) return;

      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    // Meta description
    if (pageData.meta_description) {
      setMetaTag('description', pageData.meta_description);
    }

    // Meta keywords
    if (pageData.meta_keywords) {
      setMetaTag('keywords', pageData.meta_keywords);
    }

    // Meta robots (indexation)
    if (pageData.robots_meta) {
      setMetaTag('robots', pageData.robots_meta);
    }
  };

  useEffect(() => {
    loadPage();
  }, [pagePath]);

  const loadPage = async () => {
    try {
      setLoading(true);
      setError(null);

      // Le pagePath vient de la route catch-all "*"
      // Il contient déjà le chemin complet sans le /custom/
      const cleanPath = pagePath || '';

      // Construire le chemin : ajouter le slash au début si nécessaire
      const fullPath = cleanPath ? (cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`) : '/';

      // Appeler l'API pour récupérer le contenu de la page
      const response = await api.get('/custom-pages/render/', {
        params: { path: fullPath }
      });

      setPageContent(response.data);

      // Mettre à jour le titre de la page
      if (response.data.meta_title || response.data.title) {
        document.title = response.data.meta_title || response.data.title;
      }

      // Mettre à jour les meta tags SEO
      updateMetaTags(response.data);
    } catch (err) {
      logger.error('Erreur lors du chargement de la page:', err);
      if (err.response?.status === 404) {
        setError('Page non trouvée');
      } else {
        setError('Erreur lors du chargement de la page');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Exécuter le JavaScript de la navbar
    if (pageContent?.include_navbar && pageContent?.navbar_js) {
      try {
        const navbarScript = document.createElement('script');
        navbarScript.textContent = pageContent.navbar_js;
        document.body.appendChild(navbarScript);

        return () => {
          if (navbarScript.parentNode) {
            document.body.removeChild(navbarScript);
          }
        };
      } catch (err) {
        logger.error('Erreur lors de l\'exécution du JavaScript de la navbar:', err);
      }
    }
  }, [pageContent]);

  useEffect(() => {
    // Exécuter le JavaScript du footer
    if (pageContent?.include_footer && pageContent?.footer_js) {
      try {
        const footerScript = document.createElement('script');
        footerScript.textContent = pageContent.footer_js;
        document.body.appendChild(footerScript);

        return () => {
          if (footerScript.parentNode) {
            document.body.removeChild(footerScript);
          }
        };
      } catch (err) {
        logger.error('Erreur lors de l\'exécution du JavaScript du footer:', err);
      }
    }
  }, [pageContent]);

  useEffect(() => {
    // Exécuter le JavaScript de la page après le rendu
    if (pageContent?.js_content) {
      try {
        // Créer un script et l'exécuter
        const script = document.createElement('script');
        script.textContent = pageContent.js_content;
        document.body.appendChild(script);

        // Nettoyer le script au démontage du composant
        return () => {
          if (script.parentNode) {
            document.body.removeChild(script);
          }
        };
      } catch (err) {
        logger.error('Erreur lors de l\'exécution du JavaScript:', err);
      }
    }
  }, [pageContent]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (!pageContent) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Injecter le CSS de la page si disponible */}
      {pageContent.css_content && (
        <style dangerouslySetInnerHTML={{ __html: pageContent.css_content }} />
      )}

      {/* Injecter le CSS de la navbar si elle est demandée */}
      {pageContent.include_navbar && pageContent.navbar_css && (
        <style dangerouslySetInnerHTML={{ __html: pageContent.navbar_css }} />
      )}

      {/* Injecter le CSS du footer si il est demandé */}
      {pageContent.include_footer && pageContent.footer_css && (
        <style dangerouslySetInnerHTML={{ __html: pageContent.footer_css }} />
      )}

      {/* Navbar depuis la BDD si demandée */}
      {pageContent.include_navbar && pageContent.navbar_html && (
        <div dangerouslySetInnerHTML={{ __html: pageContent.navbar_html }} />
      )}

      {/* Contenu de la page */}
      <div className="flex-grow">
        <div dangerouslySetInnerHTML={{ __html: pageContent.html_content }} />
      </div>

      {/* Footer depuis la BDD si demandé */}
      {pageContent.include_footer && pageContent.footer_html && (
        <div dangerouslySetInnerHTML={{ __html: pageContent.footer_html }} />
      )}
    </div>
  );
};

export default CustomPageViewer;
